import React, { useMemo, useCallback } from 'react'
import Board from '@cloudscape-design/board-components/board'
import {
  Container,
  Header,
  Button,
  SpaceBetween,
  Box,
  Icon
} from '@cloudscape-design/components'
import { Dashboard, DashboardWidget } from '../types'
import { ConfigurableWidget } from './ConfigurableWidget'

interface DashboardCanvasProps {
  dashboard: Dashboard
  onChange: (dashboard: Dashboard) => void
  isEditing?: boolean
}

// Board i18n strings - copied from Cloudscape demo for exact compatibility
function createAnnouncement(
  operationAnnouncement: string,
  conflicts: ReadonlyArray<any>,
  disturbed: ReadonlyArray<any>,
) {
  const conflictsAnnouncement = conflicts.length > 0 ? `Conflicts with ${conflicts.map(c => c.data.title).join(', ')}.` : '';
  const disturbedAnnouncement = disturbed.length > 0 ? `Disturbed ${disturbed.length} items.` : '';
  return [operationAnnouncement, conflictsAnnouncement, disturbedAnnouncement].filter(Boolean).join(' ');
}

const boardI18nStrings = {
  liveAnnouncementDndStarted: (operationType: string) => (operationType === 'resize' ? 'Resizing' : 'Dragging'),
  liveAnnouncementDndItemReordered: (operation: any) => {
    const columns = `column ${operation.placement.x + 1}`;
    const rows = `row ${operation.placement.y + 1}`;
    return createAnnouncement(
      `Item moved to ${operation.direction === 'horizontal' ? columns : rows}.`,
      operation.conflicts || [],
      operation.disturbed || [],
    );
  },
  liveAnnouncementDndItemResized: (operation: any) => {
    const columnsConstraint = operation.isMinimalColumnsReached ? ' (minimal)' : '';
    const rowsConstraint = operation.isMinimalRowsReached ? ' (minimal)' : '';
    const sizeAnnouncement =
      operation.direction === 'horizontal'
        ? `columns ${operation.placement.width}${columnsConstraint}`
        : `rows ${operation.placement.height}${rowsConstraint}`;
    return createAnnouncement(
      `Item resized to ${sizeAnnouncement}.`,
      operation.conflicts || [],
      operation.disturbed || [],
    );
  },
  liveAnnouncementDndCommitted: (operationType: string) => `${operationType} committed`,
  liveAnnouncementDndDiscarded: (operationType: string) => `${operationType} discarded`,
  liveAnnouncementItemRemoved: (operationType: string) => 'Item removed',
  navigationAriaLabel: 'Board navigation',
  navigationAriaDescription: 'Click on non-empty item to move focus over',
  navigationItemAriaLabel: (item: any) => (item ? item.data.title : ''),
  liveAnnouncementDndItemInserted: 'Item inserted',
  liveAnnouncementDndItemReinserted: 'Item reinserted'
}


export const DashboardCanvas: React.FC<DashboardCanvasProps> = ({
  dashboard,
  onChange,
  isEditing = false
}) => {
  // Convert dashboard widgets to board items
  const boardItems = useMemo(() => {
    return dashboard.widgets.map((widget, index) => {
      // Find existing layout or create default
      const existingLayout = dashboard.layout.find(l => l.i === widget.id)
      
      const columnSpan = existingLayout?.w || 2
      const rowSpan = existingLayout?.h || 2
      
      // Initialize with Board's native format
      const item = {
        id: widget.id,
        definition: {
          minColumnSpan: 1,
          maxColumnSpan: 12,
          minRowSpan: 2,
          maxRowSpan: 12,
          defaultColumnSpan: columnSpan,
          defaultRowSpan: rowSpan,
        },
        columnSpan,
        rowSpan,
        // Include Board's native columnOffset if available
        ...(existingLayout?.columnOffset && { columnOffset: existingLayout.columnOffset }),
        data: widget
      }
      return item
    })
  }, [dashboard.widgets, dashboard.layout])

  const handleItemsChange = useCallback(({ detail }: { detail: any }) => {
    // Only update if we're in editing mode
    if (!isEditing) return


    const { items, addedItem, removedItem, resizedItem, movedItem } = detail


    // Update widgets array to match Board items order
    const newWidgets = items.map((item: any) => item.data)

    // Store Board's native layout format (like Cloudscape demo)
    const newLayout = items.map((item: any) => ({
      i: item.id,
      w: item.columnSpan,
      h: item.rowSpan,
      // Store columnOffset in Board's native format
      columnOffset: item.columnOffset
    }))

    // Update dashboard with new state
    const updatedDashboard = {
      ...dashboard,
      widgets: newWidgets,
      layout: newLayout
    }

    onChange(updatedDashboard)
  }, [dashboard, onChange, isEditing])

  // Handle widget update
  const handleWidgetUpdate = useCallback((updatedWidget: DashboardWidget) => {
    const updatedWidgets = dashboard.widgets.map(w => 
      w.id === updatedWidget.id ? updatedWidget : w
    )
    
    const updatedDashboard = {
      ...dashboard,
      widgets: updatedWidgets
    }
    
    onChange(updatedDashboard)
  }, [dashboard, onChange])

  // Handle widget removal
  const handleWidgetRemove = useCallback((widgetId: string) => {
    const updatedWidgets = dashboard.widgets.filter(w => w.id !== widgetId)
    const updatedLayout = dashboard.layout.filter(l => l.i !== widgetId)
    
    const updatedDashboard = {
      ...dashboard,
      widgets: updatedWidgets,
      layout: updatedLayout
    }
    
    onChange(updatedDashboard)
  }, [dashboard, onChange])

  if (!dashboard.widgets || dashboard.widgets.length === 0) {
    return (
      <Container>
        <Box textAlign="center" color="text-body-secondary" padding="xxl">
          <SpaceBetween size="m">
            <Icon name="view-full" size="big" />
            <Header variant="h2">Empty Dashboard</Header>
            <div>Add widgets to start building your dashboard.</div>
            <div style={{ fontSize: '14px', color: '#879596' }}>
              Click "Add Widget" in the header to get started →
            </div>
          </SpaceBetween>
        </Box>
      </Container>
    )
  }

  return (
    <div style={{ minHeight: '400px' }}>
      <Board
        items={boardItems}
        onItemsChange={handleItemsChange}
        renderItem={(item: any) => (
          <ConfigurableWidget
            widget={item.data}
            onUpdate={handleWidgetUpdate}
            onRemove={() => handleWidgetRemove(item.data.id)}
            isEditing={isEditing}
          />
        )}
        i18nStrings={boardI18nStrings}
        empty={
          <Box textAlign="center" color="text-body-secondary">
            <SpaceBetween size="m">
              <Icon name="view-full" size="big" />
              <Header variant="h3">No widgets</Header>
              <div>Add widgets to start building your dashboard</div>
            </SpaceBetween>
          </Box>
        }
      />
    </div>
  )
}